import { useEffect, useState } from 'react';
import landingPageService, { LandingLang, LandingPageSummary } from '../services/landingPageService';
import { currentLang } from '../i18n';

// ============================================
// useLandingPages (2026-09-20) – veröffentlichte Landingpages für
// Header-Dropdown „Lösungen“, Startseiten-Teaser und Hub. EIN Request pro
// Sprache und Seitenaufruf (Modul-Cache mit geteiltem Promise, Muster
// usePlacesTop). Fehler → [] (Konsumenten blenden sich aus).
// ============================================

const cache: Partial<Record<LandingLang, LandingPageSummary[]>> = {};
const inflight: Partial<Record<LandingLang, Promise<LandingPageSummary[]>>> = {};

export const loadLandingPages = (lang: LandingLang): Promise<LandingPageSummary[]> => {
  const cached = cache[lang];
  if (cached) return Promise.resolve(cached);
  let pending = inflight[lang];
  if (!pending) {
    pending = landingPageService
      .getPages(lang)
      .then((pages) => {
        cache[lang] = Array.isArray(pages) ? pages : [];
        return cache[lang]!;
      })
      .catch(() => {
        cache[lang] = [];
        return cache[lang]!;
      })
      .finally(() => {
        delete inflight[lang];
      });
    inflight[lang] = pending;
  }
  return pending;
};

/** Nur für Tests: Cache zurücksetzen */
export const resetLandingPagesCache = (): void => {
  for (const l of Object.keys(cache) as LandingLang[]) delete cache[l];
  for (const l of Object.keys(inflight) as LandingLang[]) delete inflight[l];
};

export interface LandingPagesState {
  pages: LandingPageSummary[];
  loading: boolean;
}

/** Veröffentlichte Landingpages in der gewünschten Sprache (Default: App-Sprache). */
export default function useLandingPages(lang?: LandingLang): LandingPagesState {
  const effectiveLang: LandingLang = lang ?? currentLang();
  const [pages, setPages] = useState<LandingPageSummary[]>(() => cache[effectiveLang] ?? []);
  const [loading, setLoading] = useState<boolean>(() => cache[effectiveLang] === undefined);

  useEffect(() => {
    let cancelled = false;
    const cached = cache[effectiveLang];
    if (cached) {
      setPages(cached);
      setLoading(false);
    } else {
      setLoading(true);
    }
    loadLandingPages(effectiveLang).then((list) => {
      if (cancelled) return;
      setPages(list);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [effectiveLang]);

  return { pages, loading };
}
